import { useId } from "react";
import { Link, useParams } from "react-router";
import { ApiError } from "@/lib/api";
import type { CompanyResponse } from "@/lib/staff-api";
import { apiParams, readDir } from "@/lib/list-url";
import { formatDate, formatPhone } from "@/lib/formatters";
import { staffCan } from "@/lib/staff-capabilities";
import { useCompanyVisibility, useRefreshQueues, useStaffCompany } from "@/lib/staff-hooks";
import { useStaffAuth } from "@/lib/use-staff-auth";
import { SECONDARY_BUTTON } from "./buttons";
import { EmployeesTable } from "./EmployeesTable";
import { Loaded } from "./Loaded";
import { StageFilter, UrlTickbox } from "./ListControls";
import { OrdersTable } from "./OrdersTable";
import { Pager } from "./Pager";
import { useListUrl, usePageCorrection, useSortHandler } from "./use-list-url";

const BACK = "text-sm text-blue-700 hover:text-blue-900 hover:underline";
const LINK = "text-blue-700 hover:text-blue-900 hover:underline";

function HideButton({ id, hidden }: { id: string; hidden: boolean }): React.ReactElement {
  const mutation = useCompanyVisibility();
  const refresh = useRefreshQueues();
  const conflict = mutation.error instanceof ApiError && mutation.error.code === "CONFLICT_RELOAD";
  return (
    <div className="space-y-2">
      <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate({ id, hidden: !hidden })} className={SECONDARY_BUTTON}>
        {hidden ? "Unhide company" : "Hide company"}
      </button>
      {mutation.isError && (
        <p role="alert" className="text-sm text-red-800">
          {conflict ? "Someone else changed this company — reload." : "This company could not be changed. Try again."}
          {conflict && (
            <button
              type="button"
              className={`ml-2 ${SECONDARY_BUTTON}`}
              onClick={() => {
                mutation.reset();
                void refresh();
              }}
            >
              Reload
            </button>
          )}
        </p>
      )}
    </div>
  );
}

function CompanyBody({ data, isIt, canAct }: { data: CompanyResponse; isIt: boolean; canAct: boolean }): React.ReactElement {
  const employeesId = useId();
  const ordersId = useId();
  const [url, change] = useListUrl();
  const onSort = useSortHandler();
  const { company, employees, orders } = data;
  return (
    <div className="space-y-8">
      {company.hidden && (
        <div role="status" className="rounded-md border border-gray-400 bg-gray-100 px-4 py-3 text-sm text-gray-900">
          Hidden — only IT can see this company.
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{company.companyName}</h1>
        {isIt && <HideButton id={company.id} hidden={company.hidden} />}
      </div>
      <dl className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-600">Contact</dt>
          <dd className="mt-1">{company.contactName}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-600">Phone</dt>
          <dd className="mt-1">
            <a href={`tel:${company.contactPhone.replace(/[^\d+]/g, "")}`} className={LINK}>
              {formatPhone(company.contactPhone)}
            </a>
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-600">Email</dt>
          <dd className="mt-1">
            <a href={`mailto:${company.email}`} className={LINK}>
              {company.email}
            </a>
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-600">Registered</dt>
          <dd className="mt-1">{formatDate(company.createdAt)}</dd>
        </div>
      </dl>

      <section aria-labelledby={employeesId} className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id={employeesId} className="text-lg font-semibold text-gray-900">
            Employees <span className="font-normal text-gray-500">({employees.total})</span>
          </h2>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <UrlTickbox label="Show employees no longer employed" param="includeInactive" />
            <StageFilter />
          </div>
        </div>
        {employees.total === 0 ? (
          <p className="text-sm text-gray-600">No employees to show.</p>
        ) : (
          <EmployeesTable
            caption={`Employees of ${company.companyName}`}
            rows={employees.rows}
            sort={url.get("sort") ?? "name"}
            dir={readDir(url)}
            onSort={onSort}
            showCompany={false}
            canAct={canAct}
          />
        )}
        <Pager
          page={employees.page}
          pageSize={employees.pageSize}
          total={employees.total}
          onPage={(p) => change({ page: p === 1 ? null : String(p) })}
          label="Employees"
        />
      </section>

      <section aria-labelledby={ordersId} className="space-y-3">
        <h2 id={ordersId} className="text-lg font-semibold text-gray-900">
          Orders <span className="font-normal text-gray-500">({orders.total})</span>
        </h2>
        {orders.total === 0 ? <p className="text-sm text-gray-600">No orders yet.</p> : <OrdersTable rows={orders.rows} />}
        <Pager
          page={orders.page}
          pageSize={orders.pageSize}
          total={orders.total}
          onPage={(p) => change({ ordersPage: p === 1 ? null : String(p) })}
          label="Orders"
        />
      </section>
    </div>
  );
}

/** A company: contact details, its employees and its orders. IT may hide or
 *  unhide it; Accounting reads. */
export function StaffCompanyPage(): React.ReactElement {
  const { id = "" } = useParams();
  const { staff } = useStaffAuth();
  const isIt = staff !== null && staffCan(staff.role, "manage_staff");
  const canAct = staff !== null && staffCan(staff.role, "work_queues");
  const [url] = useListUrl();
  const params = apiParams(url, isIt);
  // IT must reach a hidden company to unhide it.
  if (isIt) params.set("includeHidden", "true");
  const query = useStaffCompany(id, params);
  usePageCorrection("page", query.isPlaceholderData ? undefined : query.data?.employees.page);
  usePageCorrection("ordersPage", query.isPlaceholderData ? undefined : query.data?.orders.page);
  const notFound = query.error instanceof ApiError && query.error.status === 404;
  return (
    <div className="space-y-6">
      <Link to="/staff/companies" className={BACK}>
        ← Companies
      </Link>
      {notFound ? (
        <h1 className="text-2xl font-bold text-gray-900">Company not found</h1>
      ) : (
        // Keyed by company: a change error on one company must not follow IT to the next.
        <Loaded queries={[query]}>{() => <CompanyBody key={id} data={query.data!} isIt={isIt} canAct={canAct} />}</Loaded>
      )}
    </div>
  );
}
